import React, { useCallback, useEffect, useState } from "react";

const API_URL = window.location.origin;

function senderEmailOf(form) {
  const s = Array.isArray(form.selectedSenders) && form.selectedSenders.length
    ? form.selectedSenders[0]
    : null;
  return (
    (s && (s.email || s.senderEmail || s.fromEmail)) ||
    form.fromEmail ||
    ""
  );
}

const LEVEL_META = {
  good: { label: "Good \u2014 inbox-ready", cls: "sx-spam-good" },
  warning: { label: "Needs improvement", cls: "sx-spam-warn" },
  bad: { label: "High spam risk", cls: "sx-spam-bad" }
};

function AuthBadge({ ok, label }) {
  return (
    <span className={"sx-spam-badge " + (ok ? "ok" : "no")}>
      {ok ? "\u2713" : "\u2717"} {label}
    </span>
  );
}

export default function SpamSafetyCheck({ form }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const runCheck = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/deliverability/check`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: form.subject || "",
          html: form.html || "",
          fromEmail: senderEmailOf(form)
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Check failed");
      setResult(data);
    } catch (err) {
      setError(err.message || "Spam check failed");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    runCheck();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const meta = result ? LEVEL_META[result.level] || LEVEL_META.warning : null;
  const auth = result && result.auth;
  const issues = (result && result.content && result.content.issues) || [];

  return (
    <div className="sx-spam-card">
      <div className="sx-spam-head">
        <div>
          <h3>Spam Safety Check</h3>
          <p>
            Bhejne se pehle ye check kar leta hai ke kya kya spam risk hai. 100%
            inbox guarantee mumkin nahi, par score jitna high, utna behtar.
          </p>
        </div>
        <button type="button" className="sx-spam-recheck" onClick={runCheck} disabled={loading}>
          {loading ? "Checking\u2026" : "Re-check"}
        </button>
      </div>

      {error && <div className="sx-spam-error">{error}</div>}

      {result && (
        <>
          <div className={"sx-spam-score " + (meta ? meta.cls : "")}>
            <div className="sx-spam-score-num">{result.score}<small>/100</small></div>
            <div className="sx-spam-score-label">{meta ? meta.label : ""}</div>
          </div>

          {auth && (
            <div className="sx-spam-section">
              <h4>Domain Authentication</h4>
              {auth.freeMailbox ? (
                <p className="sx-spam-note">
                  Aap ek free mailbox ({auth.domain}) se bhej rahe hain. Bulk mails
                  ke liye apna verified domain use karein.
                </p>
              ) : !auth.exists ? (
                <p className="sx-spam-note">
                  Domain <strong>{auth.domain || "\u2014"}</strong> abhi add/verify
                  nahi hua. Settings \u2192 Domains mein add karke verify karein.
                </p>
              ) : (
                <div className="sx-spam-badges">
                  <AuthBadge ok={auth.dkimVerified} label="DKIM" />
                  <AuthBadge ok={auth.spfVerified} label="SPF" />
                  <AuthBadge ok={auth.dmarcVerified} label="DMARC" />
                  <AuthBadge ok={auth.trackVerified} label="Tracking" />
                </div>
              )}
            </div>
          )}

          {result.warmup && result.warmup.note && (
            <div className="sx-spam-section">
              <h4>Warm-up</h4>
              <p className="sx-spam-note">{result.warmup.note}</p>
            </div>
          )}

          <div className="sx-spam-section">
            <h4>Content Issues {issues.length ? `(${issues.length})` : ""}</h4>
            {issues.length === 0 ? (
              <p className="sx-spam-note sx-spam-ok">Koi content issue nahi mila. \uD83D\uDC4D</p>
            ) : (
              <ul className="sx-spam-issues">
                {issues.map((it, i) => (
                  <li key={i} className={"sev-" + it.severity}>
                    <span className="sx-spam-dot" /> {it.text}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {Array.isArray(result.tips) && result.tips.length > 0 && (
            <div className="sx-spam-section">
              <h4>How to reach inbox</h4>
              <ul className="sx-spam-tips">
                {result.tips.map((t, i) => (
                  <li key={i}>{t}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
